import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 btn-modern",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-modern hover:shadow-modern-lg hover:scale-[1.02] active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-modern hover:shadow-modern-lg",
        outline:
          "border border-input bg-background text-foreground hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] shadow-modern hover:shadow-modern-lg",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[#1E293B] hover:text-white shadow-modern hover:shadow-modern-lg",
        ghost: "hover:bg-[#0F172A]/10 hover:text-[#0F172A]",
        link: "text-primary underline-offset-4 hover:underline hover:text-[#0F172A]",
        gradient: "bg-gradient-to-r from-primary to-purple-600 text-primary-foreground hover:from-primary/90 hover:to-purple-600/90 shadow-modern hover:shadow-glow",
        glass: "glass-card text-foreground hover:bg-[#0F172A]/10 dark:hover:bg-[#1E293B]/50",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-modern hover:shadow-modern-lg",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-modern hover:shadow-modern-lg",
        info: "bg-info text-info-foreground hover:bg-info/90 shadow-modern hover:shadow-modern-lg",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    const content = loading ? (
      <>
        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        {children}
      </>
    ) : children
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      >
        {content}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }